import classnames from 'classnames';
import { StatusTagProps, StatusTags } from '../StatusTag/StatusTag';
import { Stack, Avatar, Box } from '@mui/material';
import { Typography } from '@design-system/components/typography/typography';
import { Link } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { ColorsVars, textColorsVars } from '@design-system/vars.css';

export type CellHeadProps = {
  type: 'Head';
  value: {
    iconTooltip?: string;
    icon?: IconProp;
    iconColor?: ColorsVars;
    url?: string;
    title: string;
    subtitle?: string | Array<StatusTagProps>;
    tags?: Array<StatusTagProps>;
    link?: {
      to: string;
      label: string;
      external?: boolean;
    };
    showOrder?: boolean;
    order?: number;
  };
};

const CellHeadImage = (p: CellHeadProps['value']) => {
  if (!p.url) return null;

  const image = (
    <div style={{ position: 'relative' }}>
      {p.icon && (
        <FontAwesomeIcon
          icon={p.icon}
          style={{
            color: p.iconColor ? textColorsVars[p.iconColor] : 'black',
            position: 'absolute',
            bottom: 8,
            left: 8,
            zIndex: 90,
          }}
        />
      )}
      <Avatar src={p.url} sx={{ width: 64, height: 64 }} variant="rounded" />
    </div>
  );

  if (p.iconTooltip) {
    return (
      <Tooltip
        title={p.iconTooltip || null}
        variant="dark"
        arrow
        tooltipProps={{
          maxWidth: '145px',
        }}
      >
        {image}
      </Tooltip>
    );
  }

  return image;
};

export const CellHead = (p: CellHeadProps['value']) => (
  <Stack
    alignItems="center"
    direction="row"
    spacing={2}
    className={classnames('w-full', {
      'h-[64px]': !!p.url,
      'h-full': !p.url,
    })}
  >
    <CellHeadImage {...p} />
    {p.showOrder && typeof p.order === 'number' ? (
      <Box
        bgcolor="#161E32"
        sx={{
          width: 46,
          height: 46,
          flexShrink: 0,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body" as="span" className="text-white">
          {p.order}
        </Typography>
      </Box>
    ) : null}
    <div
      className={
        'h-full w-full flex flex-col ' + p.subtitle
          ? 'justify-between'
          : 'justify-center'
      }
    >
      <div className="flex items-center gap-2">
        <Typography variant="body" size="large">
          {p.title}
        </Typography>
        {p.tags?.length ? <StatusTags tags={p.tags} /> : null}
      </div>
      {!p.subtitle ? null : typeof p.subtitle === 'string' ? (
        <Typography variant="body" size="small" className="mt-1">
          {p.subtitle}
        </Typography>
      ) : (
        <StatusTags tags={p.subtitle} />
      )}
      {!p.link ? null : p.link.external ? (
        <a href={p.link.to} target="_blank" rel="noreferrer">
          <Typography
            variant="body"
            size="medium"
            className="text-black underline font-normal"
          >
            {p.link.label}
          </Typography>
        </a>
      ) : (
        <Link to={p.link.to}>
          <Typography
            variant="body"
            size="medium"
            className="text-black underline font-normal"
          >
            {p.link.label}
          </Typography>
        </Link>
      )}
    </div>
  </Stack>
);

export const cellHead = (value: CellHeadProps['value']): CellHeadProps => ({
  type: 'Head',
  value,
});
